import { Button, Dropdown, Segmented, Tooltip } from "antd";
import type { MenuProps } from "antd";
import {
  Def,
  modDefsOrGroups,
  IGroupsDict,
  dropDownGroups,
} from "../mod-definitions";
import { useMst } from "../../../stores/main";
import { observer } from "mobx-react-lite";
import {
  ConnectorIcon,
  AddPlusIcon,
  LayersIcon,
} from "../../../icons-and-animations/Icons";
import { UnorderedListOutlined } from "@ant-design/icons";
import "./ModButtons.scss";
import {
  getPlayerVersionRequirementMessage,
  meetsPlayerVersion,
} from "../../../helpers/playerVersion";

export type SidebarView = "connectors" | "layers";

interface NewModButtonsListProps {
  activeView?: SidebarView;
  onViewChange?: (view: SidebarView) => void;
  onConnectorMenuOpen?: () => void;
  showViewToggle?: boolean;
  className?: string;
}

export const isDisabledModButton = (
  playerStore,
  disabled: Def<any>["disabled"],
  selectedProject
) => {
  return (
    !playerStore.jsonFile ||
    (typeof disabled === "boolean" && disabled) ||
    (typeof disabled === "function" && disabled(selectedProject))
  );
};

export const NewModButtonsList = observer(
  ({
    activeView = "connectors",
    onViewChange,
    onConnectorMenuOpen,
    showViewToggle = true,
    className,
  }: NewModButtonsListProps) => {
    const {
      modsStore,
      playerStore,
      platformStore: { projectWorkspaceVersion, selectedProjectBasicInfo },
      accountStore: { isBlingsAccount },
    } = useMst();

    const menuItems = modDefsOrGroups
      .map((changeDefOrGroupOfDefs, i) => {
        if ((changeDefOrGroupOfDefs as Def<any>).ChangeFormComp) {
          // Individual mod item
          const def = changeDefOrGroupOfDefs as Def<any>;
          const disabled = isDisabledModButton(
            playerStore,
            def.disabled,
            projectWorkspaceVersion
          );

          return {
            key: `mod-${i}`,
            label: (
              <div className="connector-menu-item">
                <div className="connector-menu-item__icon">{def.Icon}</div>
                <span>{def.text}</span>
              </div>
            ),
            onClick: () => {
              if (!disabled) {
                modsStore.addMod(def.change);
              }
            },
            disabled,
          };
        } else if (
          (changeDefOrGroupOfDefs as IGroupsDict).type === "dropDownGroup"
        ) {
          // Group with sub-menu
          const group = changeDefOrGroupOfDefs as IGroupsDict;
          const groupConfig = dropDownGroups[group.name];

          const children = group.defs
            .filter(
              (def) => isBlingsAccount || def.change.type !== "embedElement"
            )
            .map((def, key) => {
              const enabled = meetsPlayerVersion(
                selectedProjectBasicInfo?.playerVersionToUse,
                def.fromVersion
              );

              const content = (
                <div className="connector-menu-item">
                  <div className="connector-menu-item__icon">
                    <ConnectorIcon type={def.change.type} />
                  </div>
                  <span>{def.text}</span>
                </div>
              );

              return {
                key: `${group.name}-${key}`,
                label: enabled ? (
                  content
                ) : (
                  <Tooltip
                    title={getPlayerVersionRequirementMessage(
                      selectedProjectBasicInfo?.playerVersionToUse,
                      def.fromVersion
                    )}
                  >
                    {content}
                  </Tooltip>
                ),
                onClick: () => {
                  if (enabled) {
                    modsStore.addMod(def.change);
                  }
                },
                disabled: !enabled,
              };
            });

          return {
            key: `group-${i}`,
            label: (
              <div className="connector-menu-item">
                <div className="connector-menu-item__icon">
                  {groupConfig.Icon}
                </div>
                <span>{groupConfig.dropDownText}</span>
              </div>
            ),
            children,
          };
        }
        return null;
      })
      .filter(Boolean) as MenuProps["items"];

    const menuProps: MenuProps = {
      theme: "dark",
      items: menuItems,
    };

    const containerClassName = [
      "add-connector-container",
      !showViewToggle ? "add-connector-container--compact" : "",
      className,
    ]
      .filter(Boolean)
      .join(" ");

    const handleViewChange = onViewChange ?? (() => {});
    const handleConnectorMenuOpenChange = (open: boolean) => {
      if (open) {
        onConnectorMenuOpen?.();
      }
    };

    return (
      <div className={containerClassName}>
        {showViewToggle && (
          <Segmented
            size="small"
            value={activeView}
            onChange={(value) => handleViewChange(value as SidebarView)}
            options={[
              {
                value: "connectors",
                icon: <UnorderedListOutlined />,
                label: "Connectors",
              },
              {
                value: "layers",
                icon: <LayersIcon width={14} height={14} />,
                label: "Layers",
              },
            ]}
            className="view-toggle"
          />
        )}
        {activeView === "connectors" && (
          <Dropdown
            menu={menuProps}
            trigger={["click"]}
            onOpenChange={handleConnectorMenuOpenChange}
            overlayClassName="add-connector-dropdown"
            popupRender={(menuNode) => (
              <div className="add-connector-dropdown-submenu">{menuNode}</div>
            )}
          >
            <Button type="primary" className="secondary add-connector-button">
              <AddPlusIcon />
            </Button>
          </Dropdown>
        )}
      </div>
    );
  }
);
